/* eslint-disable @typescript-eslint/ban-ts-comment */
/*
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：共有メソッドを提供するサービス
 *
 * 作成者：2022/1/6 ノア）黄
 * 最終修正者：2022/1/6 ノア）黄
 *
 */
import { find, toNumber, isNumber, toString } from 'lodash'

import { TreeData, BeforeTreeData, AllocationOfficeItem, DMCObject, DMObject } from '@/models'
import { MIMEType, AccessToken } from '@/commons/constants'
import { removeCookie } from '@/utils/cookieUtils'

/**
 * フォーマット定義
 *
 * @interface
 */
interface definitionsFormat {
  code: string
  value: string
}

/**
 * 共有メソッドを提供するサービス
 *
 * @class
 */
class HelperService {
  /**
   * ツリーデータのレベルが同じかどうかを判定する
   *
   * @param {TreeData} tree 地図の物体のタイプ
   * @returns {boolean} 同じかどうかの判定
   */
  isAreaLevelDifferent(tree: TreeData[]): boolean {
    if (!tree || tree.length === 0) {
      return false
    }

    const level = tree[0].level
    for (const item of tree) {
      if (item.level !== level) {
        return true
      }
    }

    return false
  }

  /**
   * リストが変更されましたかを返す
   *
   * @param {Array<any>} oldData 古いデータ
   * @param {Array<any>} newData 新しいデータ
   * @returns {boolean} isChanged
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isTableDataChanged(oldData: Array<any>, newData: Array<any>): boolean {
    if (!oldData || !newData) {
      return false
    }

    if (oldData.length !== newData.length) {
      return true
    }

    let changed = false
    for (let i = 0; i < oldData.length; i++) {
      const oldItem = oldData[i]
      const newItem = newData[i]
      const oldEntries = Object.entries(oldItem)
      const newEntries = Object.entries(newItem)

      if (JSON.stringify(oldEntries) !== JSON.stringify(newEntries)) {
        changed = true
        break
      }
    }

    return changed
  }

  /**
   * リストが変更されましたかを返す
   *
   * @param {object} oldData 古いデータ
   * @param {object} newData 新しいデータ
   * @returns {boolean} isChanged
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // eslint-disable-next-line @typescript-eslint/ban-types
  isObjectDataChanged(oldData: object, newData: object): boolean {
    if (!oldData || !newData) {
      return false
    }

    const oldEntries = Object.entries(oldData)
    const newEntries = Object.entries(newData)
    if (JSON.stringify(oldEntries) !== JSON.stringify(newEntries)) {
      return true
    }

    return false
  }

  /**
   * ファイル出力
   *
   * @param {string} fileName ファイル名
   * @param {Blob} blob ファイル
   * @param {string} mimeType MIMEタイプ
   * @returns {void}
   */
  download(fileName: string, blob: Blob, mimeType = MIMEType.csv): void {
    if (!blob) {
      return
    }

    // @ts-ignore
    if (window.navigator.msSaveOrOpenBlob) {
      // @ts-ignore
      window.navigator.msSaveOrOpenBlob(blob, fileName)
    } else {
      const a = document.createElement('a')
      const data = []
      data.push(blob)
      a.href = URL.createObjectURL(new Blob(data, { type: mimeType }))
      a.style.display = 'none'
      a.innerHTML = 'downloading'
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    }
  }

  /**
   * メッセージリストを文字列に変換する
   *
   * @param {string[]} messageList ファイル名
   * @returns {string} メッセージ文字列
   */
  changeMessageListToString(messageList: string[]): string {
    let message = ''
    messageList = [...new Set(messageList)]
    for (const item of messageList) {
      message += item + '\n'
    }

    return message
  }

  /**
   * 千分記号
   *
   * @param {string} str 文字列
   * @returns {string} 文字列
   */
  getThousandSeparator(str: string): string {
    if (!str) {
      return ''
    }

    const reg = /(?!^)(?=(\d{3})+$)/g
    return str.toString().replace(reg, ',')
  }

  /**
   * 汎用定義 value => code
   *
   * @param {Array<any>} definitions 汎用定義
   * @param {string} value 値
   * @param {string} format フォーマット
   * @returns {string} コード
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getDefinitionCode(definitions: Array<any>, value: string, format?: definitionsFormat) {
    if (!definitions) {
      return value || ''
    }

    if (!value) {
      return ''
    }

    if (!format) {
      const definition = find(definitions, { value: value })
      return definition ? definition['code'] : value
    } else {
      const key = format.value
      const definition = find(definitions, { [key]: value.toString() })
      return definition ? definition[format.code] : value
    }
  }

  /**
   * 汎用定義 code => value
   *
   * @param {Array<any>} definitions 汎用定義
   * @param {string} code コード
   * @param {string} format フォーマット
   * @returns {string} 値
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getDefinitionName(definitions: Array<any>, code: string, format?: definitionsFormat) {
    if (!definitions) {
      return code || ''
    }

    if (!code) {
      return ''
    }

    if (!format) {
      const definition = find(definitions, { code: code.toString() })
      return definition ? definition['value'] : code
    } else {
      const key = format.code
      const definition = find(definitions, { [key]: code.toString() })
      return definition ? definition[format.value] : code
    }
  }

  /**
   * 営業所データ形式の変換
   *
   * @param {AllocationOfficeItem[]} officeMasterData 営業所マスタデータ
   * @returns {TreeData[]} 営業所Treeデータ
   */
  formatofficeData(officeMasterData: AllocationOfficeItem[]): TreeData[] {
    const fillterOfficeData: BeforeTreeData[] = officeMasterData.map(
      ({ businessOfficeId, businessOfficeName, businessOfficeCode, parentId, disabled }) => {
        return {
          id: businessOfficeId,
          parentId,
          label: `${businessOfficeName}(${businessOfficeCode})`,
          disabled: disabled || false
        } as BeforeTreeData
      }
    )

    const treeData: TreeData[] = []

    if (!Array.isArray(fillterOfficeData)) {
      return treeData
    }

    const map: Record<string, TreeData> = {}

    fillterOfficeData.forEach(item => {
      if (item.id) {
        map[item.id] = item
      }
    })

    fillterOfficeData.forEach(item => {
      const parent = map[item.parentId]
      if (parent) {
        ;(parent.children || (parent.children = [])).push(item)
      } else {
        treeData.push(item)
      }
    })

    return treeData
  }

  /**
   * 緯度または経度を十進度形式から度分秒形式のDMオブジェクトに変換
   *
   * @param {number | string} deg 十進度形式
   * @returns {string} 度分形式
   */
  degToDm(deg: number | string): string {
    if (!ZDC || !ZDC.degTodms) {
      return ''
    }

    const dmcObject = ZDC.degTodms(toNumber(deg)) as DMCObject

    const dmObject = {} as DMObject
    dmObject.deg = dmcObject && dmcObject.deg
    let minResult = dmcObject.min + dmcObject.sec / 60
    if (!isNumber(minResult)) {
      minResult = 0
    }
    dmObject.min = minResult

    let dmStr = ''
    dmStr = toString(dmObject.deg) + '.' + toString(dmObject.min)

    return dmStr
  }

  /**
   * 緯度または経度を度分形式から十進度形式に変換する
   *
   * @param {string} dmObjectString 度分形式
   * @returns {number} 十進度形式
   */
  dmToDeg(dmObjectString: string): number {
    if (!dmObjectString || !ZDC || !ZDC.dmsTodeg) return 0

    const dmcArr = dmObjectString.split('.')

    const dmcObject = {} as DMCObject

    dmcObject.deg = toNumber(dmcArr[0] || 0)
    dmcObject.min = toNumber(dmcArr[1] || 0)
    const tempSec = '0.' + (dmcArr[2] || '0')
    dmcObject.sec = toNumber(tempSec) * 60

    const { deg, min, sec } = dmcObject

    const resultDeg = ZDC.dmsTodeg(deg, min, sec) as number

    return isNumber(resultDeg) ? resultDeg : 0
  }

  /**
   * 画像リンクから画像名を取得する
   *
   * @param {string} imagePath 画像リンク
   * @returns {string} 画像名
   */
  getImageNameFromLink(imagePath: string): string {
    let imageNameStr = ''

    if (!imagePath) return imageNameStr

    const strArr = imagePath.split('?')
    if (strArr && strArr.length > 0) {
      const imageUrl = strArr[0]
      const imageNameArr = imageUrl.split('/')

      if (imageNameArr && imageNameArr.length > 0) {
        imageNameStr = imageNameArr[imageNameArr.length - 1]
      }
    }

    return imageNameStr
  }

  /**
   * すべての親ノードを取得する
   *
   * @param {any[]} arr ツリーデータ
   * @param {string} id 選択したノード
   * @returns {any[]} すべての親ノード
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getParentData(arr: any[], id: string): any[] {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const temp: any[] = []
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const callback = (nowArr: any[], id: string) => {
      for (let i = 0; i < nowArr.length; i++) {
        const item = nowArr[i]
        if (item.id === id) {
          temp.push(item)
          callback(arr, item.parentId)
          break
        } else {
          if (item.children) {
            callback(item.children, id)
          }
        }
      }
    }
    callback(arr, id)
    return temp
  }

  /**
   * accesstokenを削除する
   *
   * @returns {void}
   */
  removeAccessToken(): void {
    removeCookie(AccessToken)
  }
}

// export as singleton
export default new HelperService()
